// src/api.js

import axios from 'axios';

// Token key for storing auth token
const TOKEN_KEY = 'timetracker_token';
const USER_KEY = 'timetracker_user';
const apiDebugLoggingEnabled = process.env.NODE_ENV !== 'production';

const cacheCurrentUser = (user) => {
  if (!user) return;
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch (error) {
    console.warn('Unable to cache current user:', error);
  }
};

// Global error logger (for debugging network issues)
window.onerror = function(message, source, lineno, colno, error) {
  console.log('Global error:', { message, source, lineno, colno, error });
  return false;
};

// Check if running in Capacitor (native) environment
const isNativeApp = window.Capacitor && window.Capacitor.isNativePlatform();

// Determine API URL based on env
let API_URL;
if (isNativeApp) {
  API_URL = 'https://schooner-backend.onrender.com/api/v1';
  if (apiDebugLoggingEnabled) console.log('[Capacitor Native App] Using API URL:', API_URL);
} else {
  API_URL = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
      ? 'https://schooner-backend.onrender.com/api/v1'
      : 'http://localhost:5000/api/v1');
  if (apiDebugLoggingEnabled) console.log('[Web Browser] Using API URL:', API_URL);
}

const API = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
  timeout: 30000,
});

// Request interceptor to attach token
API.interceptors.request.use(
  config => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    if (apiDebugLoggingEnabled) {
      console.log('API Request:', {
        method: config.method,
        url: config.baseURL + config.url,
      });
    }
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
API.interceptors.response.use(
  response => {
    if (apiDebugLoggingEnabled) {
      console.log('API Response success:', {
        url: response.config.baseURL + response.config.url,
        status: response.status,
      });
    }
    return response;
  },
  error => {
    console.error('API Error:', {
      url: error.config?.baseURL + error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
    });
    if (error.response?.status === 401) {
      console.log('Authentication error – clearing token and redirecting');
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ========== Auth Service ==========
export const authService = {
  login: async (email, password) => {
    console.log('Login attempt for:', email);
    const res = await API.post('/auth/login', { email, password });
    if (res.data.token) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
      cacheCurrentUser(res.data.user);
      console.log('Login successful, token stored');
    }
    return res.data;
  },
  register: (userData) => API.post('/auth/register', userData),
  logout: () => {
    console.log('Logging out user');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  forgotPassword: (email) => API.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => API.post('/auth/reset-password', { token, password }),
  verifyInvitation: (token) => API.get(`/auth/verify-invitation/${token}`),
  completeInvitation: (data) => API.post('/auth/complete-invitation', data),

  deleteAccount: () => API.delete('/users/delete-account'),
  fetchCurrentUser: async () => {
    const response = await API.get('/users/me');
    cacheCurrentUser(response.data.user || response.data);
    return response;
  },
  getCachedCurrentUser: () => {
    try {
      const user = JSON.parse(localStorage.getItem(USER_KEY) || 'null');
      return user && typeof user === 'object' ? user : null;
    } catch {
      return null;
    }
  },
  getCurrentUser: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }
};

export const appConfigService = {
  getPublic: () => API.get('/app/config', { timeout: 5000 }),
  getOwnerSettings: () => API.get('/app/owner-settings'),
  updateOwnerSettings: (features) => API.put('/app/owner-settings', { features })
};

// ========== Time Entry Service ==========
export const timeEntryService = {
  clockIn: (location = null, config = {}) => API.post('/time-entries/clock-in', location || {}, config),
  startBreak: (location = null, config = {}) => API.post('/time-entries/start-break', location || {}, config),
  endBreak: (location = null, config = {}) => API.post('/time-entries/end-break', location || {}, config),
  clockOut: (options = {}, config = {}) => API.post('/time-entries/clock-out', options, config),
  getUserEntries: (config = {}) => API.get('/time-entries/my-entries', config),
  getAllEntries: (params) => API.get('/time-entries/all', { params }),
  getActiveEmployees: () => API.get('/time-entries/active'),
  updateTimeEntry: (id, data) => API.put(`/time-entries/${id}`, data),
  restoreTimeEntry: (id) => API.post(`/time-entries/${id}/restore`),
  archiveTimeEntries: (beforeDate) => API.post('/time-entries/archive', { beforeDate }),
  unarchiveTimeEntries: (throughDate) => API.post('/time-entries/archive/restore', { throughDate }),
  // Add notes to a time entry
  addNotes: (timeEntryId, notes) => API.put(`/time-entries/${timeEntryId}/notes`, { notes }),
  // Get notes for a time entry
  getNotes: (timeEntryId) => API.get(`/time-entries/${timeEntryId}/notes`),
  // Mark a time entry as invalid (soft delete)
  invalidateTimeEntry: (id) => API.put(`/time-entries/${id}`, { 
    status: 'invalid', 
    notes: 'Entry invalidated by admin'
  }),
  createManualEntry: (data) => API.post('/time-entries/manual-entry', data),
  exportTimeEntries: (params, config = {}) =>
    API.get('/time-entries/export', { params, responseType: 'blob', ...config }),
  approveTimeEntry: (id, approved, adminNotes) =>
    API.patch(`/time-entries/${id}/approve`, { approved, adminNotes }),
  reviewLocation: (id, reviewed) =>
    API.patch(`/time-entries/${id}/location-review`, { reviewed }),
};

// ========== Invitation Service ==========
export const invitationService = {
  inviteEmployee: (data) => API.post('/invitations', data),
  getPendingInvitations: () => API.get('/invitations/pending'),
  resendPendingInvitation: (id) => API.post(`/invitations/${id}/resend`),
  deletePendingInvitation: (id) => API.delete(`/invitations/${id}`),
};

// ========== Notification Service ==========
export const notificationService = {
  getNotifications: () => API.get('/notifications'),
  markAsRead: (id) => API.put(`/notifications/${id}/read`),
  markAllAsRead: () => API.put('/notifications/read-all'),
  getUnreadCount: () => API.get('/notifications/unread-count'),
};

// ========== Report Service ==========
export const reportService = {
  submitReport: (content, timeEntryId) =>
    API.post('/reports', { content, timeEntryId }),
  getUserReports: (params) => API.get('/reports/my-reports', { params }),
  getAllReports: (params) => API.get('/reports/all', { params }),
  exportReports: (params) =>
    API.get('/reports/export', { params, responseType: 'blob' }),
};

// ========== Mileage Service ==========
export const mileageService = {
  checkRequired: (config = {}) => API.get('/mileage/check-required', config),
  createEntry: (data) => API.post('/mileage', data),
  getUserEntries: (params) => API.get('/mileage/my-entries', { params }),
  getAllEntries: (params) => API.get('/mileage/all', { params }),
  updateEntry: (id, data) => API.put(`/mileage/${id}`, data),
  deleteEntry: (id) => API.delete(`/mileage/${id}`),
  getA30Transponders: () => API.get('/mileage/a30-transponders'),
  saveA30Transponder: (unitNumber, data) => API.put(`/mileage/a30-transponders/${encodeURIComponent(unitNumber)}`, data),
  deleteA30Transponder: (unitNumber) => API.delete(`/mileage/a30-transponders/${encodeURIComponent(unitNumber)}`),
  exportData: (params) => API.get('/mileage/export', { 
    params, 
    responseType: 'blob' 
  }),
};

// ========== Work Entry Service ==========
export const workEntryService = {
  addEntry: (data) => API.post('/work-entries', data),
  getUserEntries: (params) => API.get('/work-entries/my-entries', { params }),
  getAllEntries: (params) => API.get('/work-entries/all', { params }),
  deleteEntry: (id) => API.delete(`/work-entries/${id}`),
  updateWorkEntry: (id, data) => API.put(`/work-entries/${id}`, data),
  verifyDispatch: (id) => API.post(`/work-entries/${id}/verify-dispatch`),
  getBolDocument: (id, index) => API.get(`/work-entries/${id}/bol-document/${index}`, { responseType: 'blob', timeout: 120000 }),
  
  exportEntries: (params) => {
    console.log('Export work entries API call with params:', params);
    
    // Clean up parameters to avoid URL construction issues
    const cleanParams = {
      startDate: params.startDate,
      endDate: params.endDate,
      format: params.format || 'excel'
    };
    
    // Only add userId if it's not empty
    if (params.userId && params.userId.trim() !== '') {
      cleanParams.userId = params.userId;
    }
    
    // Only add showDeleted if it's explicitly true
    if (params.showDeleted === true) {
      cleanParams.showDeleted = 'true';
    }

    if (params.archive) {
      cleanParams.archive = params.archive;
    }
    
    console.log('Cleaned params for API call:', cleanParams);
    
    return API.get('/work-entries/export', { 
      params: cleanParams,
      responseType: 'blob',
      timeout: 60000,
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    }).catch(error => {
      console.error('Export API detailed error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
        url: error.config?.url,
        params: error.config?.params
      });
      throw error;
    });
  },
  
  restoreEntry: (id) => API.put(`/work-entries/restore/${id}`),
};

// ========== Time Fix Request Service ==========
export const timeFixRequestService = {
  createRequest: (data) => API.post('/time-fix-requests', data),
  getAllRequests: (params) => API.get('/time-fix-requests/all', { params }),
  getUserRequests: (params) => API.get('/time-fix-requests/my-requests', { params }),
  markAsFixed: (id) => API.put(`/time-fix-requests/${id}/mark-fixed`),
  rejectRequest: (id, rejectionReason) => API.put(`/time-fix-requests/${id}/reject`, { rejectionReason }),
  deleteRequest: (id) => API.delete(`/time-fix-requests/${id}`),
};

// ========== Summary Export Service ==========
export const exportService = {
  /**
   * Downloads one Excel file containing both hours and reports
   * for a given user over a date range.
   *
   * @param {Object} opts
   * @param {string} opts.userId     – employee UUID
   * @param {string} opts.startDate  – 'YYYY-MM-DD'
   * @param {string} opts.endDate    – 'YYYY-MM-DD'
   */
  exportSummary: ({ userId, startDate, endDate, company }) => {
    console.log('API call: exportSummary with params:', { userId, startDate, endDate, company });
    
    const requestData = {
      startDate,
      endDate
    };
    
    // Only include userId if it's provided and not empty
    if (userId && userId.trim() !== '') {
      requestData.userId = userId;
    }
    if (company) {
      requestData.company = company;
    }
    
    console.log('Cleaned request data:', requestData);
    
    return API.post('/export/summary', requestData, {
      responseType: 'blob',
      timeout: 60000,
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    }).catch(error => {
      console.error('Export API error:', error);
      // If POST fails, try GET as fallback
      const getParams = {
        startDate,
        endDate
      };
      
      if (userId && userId.trim() !== '') {
        getParams.userId = userId;
      }
      if (company) {
        getParams.company = company;
      }
      
      return API.get('/export/summary', {
        params: getParams,
        responseType: 'blob',
        timeout: 60000,
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
    });
  }
};

// ========== Sick Leave Service ==========
export const sickLeaveService = {
  getBalances: (year) => API.get('/sick-leave/balances', { params: { year } }),
  getEmployees: () => API.get('/sick-leave/employees'),
  createEmployee: (data) => API.post('/sick-leave/employees', data),
  updateEmployee: (id, data) => API.put(`/sick-leave/employees/${id}`, data),
  deleteEmployee: (id) => API.delete(`/sick-leave/employees/${id}`),
  addUnpaidLeave: (empId, data) => API.post(`/sick-leave/employees/${empId}/unpaid-leaves`, data),
  updateUnpaidLeave: (empId, leaveId, data) => API.put(`/sick-leave/employees/${empId}/unpaid-leaves/${leaveId}`, data),
  removeUnpaidLeave: (empId, leaveId) => API.delete(`/sick-leave/employees/${empId}/unpaid-leaves/${leaveId}`),
  getEntries: (year) => API.get('/sick-leave/entries', { params: { year } }),
  createEntry: (data) => API.post('/sick-leave/entries', data),
  deleteEntry: (id) => API.delete(`/sick-leave/entries/${id}`),
  getPayrollEntries: (startDate, endDate) => API.get('/sick-leave/payroll-entries', { params: { startDate, endDate } }),
  getStatHolidays: (year) => API.get('/sick-leave/stat-holidays', { params: { year } }),
  createStatHoliday: (data) => API.post('/sick-leave/stat-holidays', data),
  updateStatHoliday: (id, data) => API.put(`/sick-leave/stat-holidays/${id}`, data),
  deleteStatHoliday: (id) => API.delete(`/sick-leave/stat-holidays/${id}`),
  getStatHolidayDrivers: (id) => API.get(`/sick-leave/stat-holidays/${id}/drivers`),
  updateStatHolidayDriver: (holidayId, userId, data) => API.put(`/sick-leave/stat-holidays/${holidayId}/drivers/${userId}`, data),
  resetStatHolidayDriver: (holidayId, userId) => API.delete(`/sick-leave/stat-holidays/${holidayId}/drivers/${userId}`),
};

// ========== Vehicle Issue Service ==========
export const vehicleIssueService = {
  createReport: (data) => API.post('/vehicle-issues', data),
  getReports:   ()     => API.get('/vehicle-issues'),
};

// ========== Wait Time Service ==========
export const waitTimeService = {
  getActive: () => API.get('/wait-times/active'),
  getMine: () => API.get('/wait-times/mine'),
  getMineSummary: () => API.get('/wait-times/mine/summary'),
  start: (data) => API.post('/wait-times/start', data),
  createMissed: (data) => API.post('/wait-times/manual', data),
  finish: (id, data) => API.post(`/wait-times/${id}/finish`, data),
  startLoading: (id) => API.post(`/wait-times/${id}/start-loading`),
  cancel: (id) => API.post(`/wait-times/${id}/cancel`),
  updateMine: (id, data) => API.patch(`/wait-times/${id}`, data),
  deleteMine: (id) => API.delete(`/wait-times/mine/${id}`),
  updateByManager: (id, data) => API.patch(`/wait-times/${id}/manager`, data),
  getAll: (params) => API.get('/wait-times', { params }),
  getSummary: () => API.get('/wait-times/summary'),
  approve: (id, dispatcherComment) => API.post(`/wait-times/${id}/approve`, { dispatcherComment }),
  process: (id, processingNote) => API.post(`/wait-times/${id}/process`, { processingNote }),
  returnForReview: (id, comment) => API.post(`/wait-times/${id}/return`, { comment }),
  delete: (id) => API.delete(`/wait-times/${id}`),
};

// ========== Dispatch Load Service (tester-only) ==========
export const dispatchLoadService = {
  getMine: (config = {}) => API.get('/dispatch-loads', config),
  reportEvent: (loadId, data, idempotencyKey) => API.post(
    `/dispatch-loads/${encodeURIComponent(loadId)}/events`,
    data,
    { headers: { 'Idempotency-Key': idempotencyKey } }
  ),
};

// ========== Performance Bonus Service ==========
export const bonusService = {
  getDrivers: () => API.get('/bonus/drivers'),
  calculate: (quarter, year) => API.get('/bonus/calculate', { params: { quarter, year } }),
  getIncidents: (quarter, year) => API.get('/bonus/incidents', { params: { quarter, year } }),
  createIncident: (data) => API.post('/bonus/incidents', data),
  updateIncident: (id, data) => API.put(`/bonus/incidents/${id}`, data),
  deleteIncident: (id) => API.delete(`/bonus/incidents/${id}`),
  getDeletedIncidents: (quarter, year) => API.get('/bonus/incidents/deleted', { params: { quarter, year } }),
  restoreIncident: (id) => API.post(`/bonus/incidents/${id}/restore`),
  getStatuses: (quarter, year) => API.get('/bonus/statuses', { params: { quarter, year } }),
  setStatus: (data) => API.post('/bonus/statuses', data),
  deleteStatus: (id) => API.delete(`/bonus/statuses/${id}`),
  getDeletedStatuses: (quarter, year) => API.get('/bonus/statuses/deleted', { params: { quarter, year } }),
  restoreStatus: (id) => API.post(`/bonus/statuses/${id}/restore`),
};

export default API;
